import { useState, useCallback, useMemo } from 'react'
import { NotificationSnackbars } from 'src/v3/components/notification-snackbars'

/**
 * Hook to manage error and success notifications across v4 pages
 *
 * @example
 * let { showError, showSuccess, NotificationComponent } = useNotifications()
 *
 * // Show notifications
 * showError('Failed to save')
 * showSuccess('Saved successfully')
 *
 * // Render in JSX
 * return (
 *   <>
 *     {/* ... page content ... *\/}
 *     {NotificationComponent}
 *   </>
 * )
 */
export function useNotifications() {
  let [errorMessage, setErrorMessage] = useState('')
  let [successMessage, setSuccessMessage] = useState('')

  let showError = useCallback((message: string) => {
    setErrorMessage(message)
  }, [])

  let showSuccess = useCallback((message: string) => {
    setSuccessMessage(message)
  }, [])

  let clearError = useCallback(() => {
    setErrorMessage('')
  }, [])

  let clearSuccess = useCallback(() => {
    setSuccessMessage('')
  }, [])

  let NotificationComponent = useMemo(
    () => (
      <NotificationSnackbars
        errorMessage={errorMessage}
        successMessage={successMessage}
        setErrorMessage={setErrorMessage}
        setSuccessMessage={setSuccessMessage}
      />
    ),
    [errorMessage, successMessage],
  )

  return {
    errorMessage,
    successMessage,
    showError,
    showSuccess,
    clearError,
    clearSuccess,
    NotificationComponent,
  }
}
